import React, { useCallback, useMemo, useState } from "react";
import { ChevronLeftIcon, ChevronRightIcon } from "./Icons";

// ✅ Enhanced Pagination Component with Better UX and Exclusion Support
export const Pagination = ({
	currentPage,
	totalPages,
	totalItems,
	itemsPerPage,
	onPageChange,
	showPageSizeSelector = true,
	showJumpToPage = true,
	showResultsInfo = true,
	pageSizeOptions = [25, 50, 100, 200],
	onPageSizeChange,
	maxVisiblePages = 7,
	loading = false,
	className = "",
	excludedItems = [], // Array of excluded item IDs
	originalTotalItems = null, // Original total before exclusions
	onClearExcluded = null,
	isSearching = false,
	hasSearchResults = true,
	searchType = null,
	forceShow = false,
}) => {
	const [jumpToPageInput, setJumpToPageInput] = useState("");
	const [showJumpInput, setShowJumpInput] = useState(false);

	// ✅ Calculate exclusion info
	const excludedCount = excludedItems.length;
	const effectiveOriginalTotal =
		originalTotalItems || totalItems + excludedCount;
	const hasExclusions = excludedCount > 0;

	// ✅ Memoized page range calculation for better performance
	const pageRange = useMemo(() => {
		const delta = Math.floor(maxVisiblePages / 2);
		const range = [];
		const rangeWithDots = [];

		// Always show first page
		const left = Math.max(2, currentPage - delta);
		const right = Math.min(totalPages - 1, currentPage + delta);

		// Add first page
		if (totalPages > 1) {
			rangeWithDots.push(1);
		}

		// Add dots if there's a gap
		if (left > 2) {
			rangeWithDots.push("...");
		}

		// Add middle pages
		for (let i = left; i <= right; i++) {
			rangeWithDots.push(i);
		}

		// Add dots if there's a gap
		if (right < totalPages - 1) {
			rangeWithDots.push("...");
		}

		// Add last page
		if (totalPages > 1) {
			rangeWithDots.push(totalPages);
		}

		return rangeWithDots;
	}, [currentPage, totalPages, maxVisiblePages]);

	// ✅ Optimized page change handler
	const handlePageChange = useCallback(
		(page) => {
			if (page >= 1 && page <= totalPages && page !== currentPage && !loading) {
				onPageChange(page);
			}
		},
		[currentPage, totalPages, onPageChange, loading],
	);

	// ✅ Jump to page functionality
	const handleJumpToPage = useCallback(() => {
		const pageNumber = parseInt(jumpToPageInput, 10);
		if (pageNumber >= 1 && pageNumber <= totalPages) {
			handlePageChange(pageNumber);
			setJumpToPageInput("");
			setShowJumpInput(false);
		}
	}, [jumpToPageInput, totalPages, handlePageChange]);

	// ✅ Keyboard navigation
	const handleKeyPress = useCallback(
		(e) => {
			if (e.key === "Enter") {
				handleJumpToPage();
			} else if (e.key === "Escape") {
				setShowJumpInput(false);
				setJumpToPageInput("");
			}
		},
		[handleJumpToPage],
	);

	// ✅ Page size change handler
	const handlePageSizeChange = useCallback(
		(newSize) => {
			if (onPageSizeChange) {
				onPageSizeChange(newSize);
			}
		},
		[onPageSizeChange],
	);

	// Calculate current results range
	const startItem = (currentPage - 1) * itemsPerPage + 1;
	const endItem = Math.min(currentPage * itemsPerPage, totalItems);

	// ✅ Don't show pagination if no pages unless forced
	//if (totalPages <= 1 && !forceShow) return null;

	return (
		<>
			<style jsx>{`
        .pagination-container {
          display: flex;
          flex-direction: column;
          gap: 8px;
          align-items: center;
          padding: 16px 20px;
          border-top: 1px solid #e5e7eb;
          background-color: #fafafa;
          border-radius: 0 0 8px 8px;
        }

        /* ✅ NEW: Main row for single-line layout */
        .pagination-main-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          width: 100%;
          gap: 20px;
          flex-wrap: nowrap;
        }

        .pagination-info {
          display: flex;
          align-items: center;
          flex-shrink: 0;
        }

        .results-text {
          font-size: 14px;
          color: #6b7280;
          font-weight: 500;
          white-space: nowrap;
        }

        /* ✅ NEW: Separate row for exclusion info */
        .exclusion-info-row {
          width: 100%;
          display: flex;
          justify-content: center;
        }

        .exclusion-info {
          font-size: 12px;
          color: #ef4444;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .clear-excluded-btn {
          font-size: 12px;
          color: #3b82f6;
          background: none;
          border: none;
          text-decoration: underline;
          cursor: pointer;
          padding: 0;
        }

        .clear-excluded-btn:hover {
          color: #1d4ed8;
        }

        .pagination-controls {
          display: flex;
          align-items: center;
          gap: 4px;
          flex-shrink: 0;
        }

        .pagination-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          min-width: 32px;
          height: 32px;
          border: 1px solid #d1d5db;
          background: white;
          color: #374151;
          border-radius: 6px;
          cursor: pointer;
          transition: all 0.2s;
          font-size: 13px;
          font-weight: 500;
        }

        .pagination-btn:hover:not(.disabled):not(.active) {
          background: #f3f4f6;
          border-color: #9ca3af;
        }

        .pagination-btn.active {
          background: #3b82f6;
          border-color: #3b82f6;
          color: white;
        }

        .pagination-btn.disabled {
          opacity: 0.4;
          cursor: not-allowed;
          pointer-events: none;
        }

        .first-btn, .last-btn {
          font-size: 16px;
          font-weight: bold;
        }

        .page-numbers {
          display: flex;
          align-items: center;
          gap: 2px;
        }

        .pagination-dots {
          padding: 0 6px;
          color: #9ca3af;
          font-weight: bold;
        }

        .pagination-advanced {
          display: flex;
          align-items: center;
          gap: 16px;
          flex-shrink: 0;
        }

        .jump-to-page {
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .jump-trigger-btn {
          padding: 4px 8px;
          background: #f3f4f6;
          border: 1px solid #d1d5db;
          border-radius: 4px;
          font-size: 11px;
          color: #374151;
          cursor: pointer;
          transition: background-color 0.2s;
          white-space: nowrap;
        }

        .jump-trigger-btn:hover:not(:disabled) {
          background: #e5e7eb;
        }

        .jump-input-container {
          display: flex;
          align-items: center;
          gap: 4px;
        }

        .jump-input {
          width: 50px;
          padding: 3px 6px;
          border: 1px solid #d1d5db;
          border-radius: 4px;
          font-size: 11px;
          text-align: center;
        }

        .jump-input:focus {
          outline: none;
          border-color: #3b82f6;
          box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
        }

        .jump-go-btn, .jump-cancel-btn {
          padding: 3px 6px;
          border: 1px solid #d1d5db;
          border-radius: 4px;
          font-size: 11px;
          cursor: pointer;
          transition: all 0.2s;
        }

        .jump-go-btn {
          background: #3b82f6;
          color: white;
          border-color: #3b82f6;
        }

        .jump-go-btn:hover:not(:disabled) {
          background: #1d4ed8;
        }

        .jump-go-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .jump-cancel-btn {
          background: #f3f4f6;
          color: #374151;
        }

        .jump-cancel-btn:hover {
          background: #e5e7eb;
        }

        .page-size-selector {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 12px;
          color: #6b7280;
          white-space: nowrap;
        }

        .page-size-select {
          padding: 3px 6px;
          border: 1px solid #d1d5db;
          border-radius: 4px;
          font-size: 11px;
          background: white;
          color: #374151;
          cursor: pointer;
        }

        .page-size-select:focus {
          outline: none;
          border-color: #3b82f6;
          box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
        }

        .pagination-loading {
          display: flex;
          align-items: center;
          gap: 8px;
          color: #6b7280;
          font-size: 12px;
        }

        .loading-spinner {
          width: 16px;
          height: 16px;
          border: 2px solid #f3f3f3;
          border-top: 2px solid #3b82f6;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        /* Mobile Styles - Convert to vertical layout on small screens */
        @media (max-width: 768px) {
          .pagination-main-row {
            flex-direction: column;
            gap: 12px;
            align-items: center;
          }

          .pagination-controls {
            order: 1;
          }

          .pagination-info {
            order: 2;
          }

          .pagination-advanced {
            order: 3;
            flex-direction: column;
            gap: 8px;
          }

          .results-text {
            font-size: 12px;
            text-align: center;
          }

          .pagination-btn {
            min-width: 28px;
            height: 28px;
            font-size: 12px;
          }

          .first-btn, .last-btn {
            font-size: 14px;
          }
        }

        @media (max-width: 480px) {
          .pagination-container {
            padding: 12px 16px;
          }

          .pagination-main-row {
            gap: 8px;
          }

          .pagination-controls {
            gap: 2px;
          }

          .pagination-btn {
            min-width: 24px;
            height: 24px;
            font-size: 11px;
          }

          .first-btn, .last-btn {
            font-size: 12px;
          }

          .results-text {
            font-size: 11px;
          }

          .exclusion-info {
            font-size: 10px;
            text-align: center;
          }
        }
      `}</style>

			<div className={`pagination-container ${className}`}>
				{/* ✅ Single line layout with results info, controls, and advanced options */}
				<div className="pagination-main-row">
					{/* Results Information */}
					{showResultsInfo && (
						<div className="pagination-info">
							<span className="results-text">
								Showing {startItem.toLocaleString()} -{" "}
								{endItem.toLocaleString()} of {totalItems.toLocaleString()}{" "}
								results
							</span>
						</div>
					)}

					{/* Pagination Controls */}
					<div className="pagination-controls">
						{/* ✅ First Page Button */}
						<button
							className={`pagination-btn first-btn ${currentPage === 1 || loading ? "disabled" : ""}`}
							onClick={() => handlePageChange(1)}
							disabled={currentPage === 1 || loading}
							title="First page"
						>
							⟪
						</button>

						{/* ✅ Previous Page Button */}
						<button
							className={`pagination-btn prev-btn ${currentPage === 1 || loading ? "disabled" : ""}`}
							onClick={() => handlePageChange(currentPage - 1)}
							disabled={currentPage === 1 || loading}
							title="Previous page"
						>
							<ChevronLeftIcon />
						</button>

						{/* ✅ Page Numbers with Smart Range */}
						<div className="page-numbers">
							{pageRange.map((page, index) => (
								<React.Fragment key={index}>
									{page === "..." ? (
										<span className="pagination-dots">...</span>
									) : (
										<button
											className={`pagination-btn page-btn ${page === currentPage ? "active" : ""} ${loading ? "disabled" : ""}`}
											onClick={() => handlePageChange(page)}
											disabled={loading}
											title={`Go to page ${page}`}
										>
											{page}
										</button>
									)}
								</React.Fragment>
							))}
						</div>

						{/* ✅ Next Page Button */}
						<button
							className={`pagination-btn next-btn ${currentPage === totalPages || loading ? "disabled" : ""}`}
							onClick={() => handlePageChange(currentPage + 1)}
							disabled={currentPage === totalPages || loading}
							title="Next page"
						>
							<ChevronRightIcon />
						</button>

						{/* ✅ Last Page Button */}
						<button
							className={`pagination-btn last-btn ${currentPage === totalPages || loading ? "disabled" : ""}`}
							onClick={() => handlePageChange(totalPages)}
							disabled={currentPage === totalPages || loading}
							title="Last page"
						>
							⟫
						</button>
					</div>

					{/* Advanced Controls */}
					<div className="pagination-advanced">
						{/* Jump to Page */}
						{showJumpToPage && totalPages > 1 && (
							<div className="jump-to-page">
								{!showJumpInput ? (
									<button
										className="jump-trigger-btn"
										onClick={() => setShowJumpInput(true)}
										disabled={loading}
									>
										Jump to page
									</button>
								) : (
									<div className="jump-input-container">
										<input
											type="number"
											min="1"
											max={totalPages}
											value={jumpToPageInput}
											onChange={(e) => setJumpToPageInput(e.target.value)}
											onKeyPress={handleKeyPress}
											placeholder={`1-${totalPages}`}
											className="jump-input"
											autoFocus
										/>
										<button
											onClick={handleJumpToPage}
											disabled={!jumpToPageInput || loading}
											className="jump-go-btn"
										>
											Go
										</button>
										<button
											onClick={() => {
												setShowJumpInput(false);
												setJumpToPageInput("");
											}}
											className="jump-cancel-btn"
										>
											×
										</button>
									</div>
								)}
							</div>
						)}

						{/* Page Size Selector */}
						{showPageSizeSelector && (
							<div className="page-size-selector">
								<label htmlFor="pageSize">Show:</label>
								<select
									id="pageSize"
									value={itemsPerPage}
									onChange={(e) =>
										handlePageSizeChange(parseInt(e.target.value, 10))
									}
									disabled={loading}
									className="page-size-select"
								>
									{pageSizeOptions.map((size) => (
										<option key={size} value={size}>
											{size} per page
										</option>
									))}
								</select>
							</div>
						)}
					</div>
				</div>

				{/* ✅ Exclusion info on separate line if exists */}
				{hasExclusions && (
					<div className="exclusion-info-row">
						<div className="exclusion-info">
							<span>
								{excludedCount} item{excludedCount !== 1 ? "s" : ""} excluded
								{originalTotalItems &&
									` (${effectiveOriginalTotal.toLocaleString()} total originally)`}
							</span>
							{onClearExcluded && (
								<button
									className="clear-excluded-btn"
									onClick={onClearExcluded}
									title="Show all excluded items again"
								>
									Show all
								</button>
							)}
						</div>
					</div>
				)}

				{/* ✅ Loading Indicator */}
				{loading && (
					<div className="pagination-loading">
						<div className="loading-spinner"></div>
						<span>Loading...</span>
					</div>
				)}
			</div>
		</>
	);
};

// ✅ Enhanced Hook for Pagination Logic with Exclusion Support
export const usePagination = (
	data,
	initialPageSize = 25,
	excludedItems = [],
) => {
	const [currentPage, setCurrentPage] = useState(1);
	const [pageSize, setPageSize] = useState(initialPageSize);
	const [loading, setLoading] = useState(false);

	// ✅ Filter out excluded items
	const filteredData = useMemo(() => {
		if (!data || !Array.isArray(data)) return [];
		return data.filter((item) => {
			const itemId = item.id || item.domainId;
			return !excludedItems.includes(itemId);
		});
	}, [data, excludedItems]);

	const totalItems = filteredData.length;
	const originalTotalItems = data?.length || 0;
	const totalPages = Math.ceil(totalItems / pageSize);

	// Reset to first page when page size changes or exclusions change
	const handlePageSizeChange = useCallback((newSize) => {
		setPageSize(newSize);
		setCurrentPage(1);
	}, []);

	// Handle page change with loading state
	const handlePageChange = useCallback(async (page) => {
		setLoading(true);

		// Simulate API delay (remove in production)
		await new Promise((resolve) => setTimeout(resolve, 100));

		setCurrentPage(page);
		setLoading(false);

		// Scroll to top of results
		window.scrollTo({ top: 0, behavior: "smooth" });
	}, []);

	// Reset to page 1 when exclusions change significantly
	React.useEffect(() => {
		if (currentPage > totalPages && totalPages > 0) {
			setCurrentPage(1);
		}
	}, [currentPage, totalPages]);

	// Get current page data
	const currentData = useMemo(() => {
		const startIndex = (currentPage - 1) * pageSize;
		const endIndex = startIndex + pageSize;
		return filteredData.slice(startIndex, endIndex);
	}, [filteredData, currentPage, pageSize]);

	return {
		currentPage,
		pageSize,
		totalPages,
		totalItems,
		originalTotalItems,
		currentData,
		filteredData,
		loading,
		handlePageChange,
		handlePageSizeChange,
		setCurrentPage,
	};
};
